// PRD.324 — measuring a Rich Text document for pagination.
//
// The layout rules live in `layoutRichTextFlow` (pure, unit-tested). This is
// the only piece that genuinely needs a DOM: how tall does each block render at
// the mapping's actual width?
//
// It renders the document offscreen at the exact target pixel width, measures
// each block, and reports heights as a PERCENTAGE OF PAGE HEIGHT — the same
// unit every mapping already uses, so the engine needs no conversion.
//
// Measuring BLOCKS rather than the whole document is what lets pagination break
// between paragraphs and list items instead of mid-line.

import { useEffect, useRef } from 'react';
import { type FlowBlock, type RichTextDoc } from '@helios/data-access';
import { RichTextView } from './RichTextField';

export function RichTextFlowMeasurer({
  doc,
  widthPx,
  pageHeightPx,
  onMeasured,
}: {
  doc: RichTextDoc;
  /** Rendered width of the mapping box, in CSS pixels. */
  widthPx: number;
  /** Rendered height of one page, in CSS pixels. */
  pageHeightPx: number;
  onMeasured: (blocks: FlowBlock[]) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || widthPx <= 0 || pageHeightPx <= 0) return;
    const children = Array.from(el.children) as HTMLElement[];
    onMeasured(
      children.map((child, index) => ({
        index,
        heightPct: (child.getBoundingClientRect().height / pageHeightPx) * 100,
      })),
    );
    // `doc` is compared by identity; callers memoize it from the stored string.
  }, [doc, widthPx, pageHeightPx, onMeasured]);

  return (
    <div
      ref={ref}
      aria-hidden
      style={{
        position: 'absolute',
        // Offscreen rather than `display: none`, which would report zero
        // heights, and rather than `visibility: hidden`, which would still
        // occupy layout space in the page.
        left: -99999,
        top: 0,
        width: widthPx,
        pointerEvents: 'none',
      }}
    >
      {/* One wrapper per block, so each is measured independently at the same
          width and typography the real render uses. */}
      {doc.blocks.map((block, i) => (
        <div key={i}>
          <RichTextView doc={{ version: 1, blocks: [block] }} />
        </div>
      ))}
    </div>
  );
}
